import { DialogM } from "@common/components/alert/dialog/Dialog";
import FilledButtonM from "@common/components/button/FilledButtonM";
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender";
import { useApiStatus } from "@common/hooks/useApiStatus";
import { useCamera } from "@common/hooks/useCamera";
import useFinalizeSilentlyIfHasMedia from "@common/hooks/useFinalizeSilentlyIfHasMedia";
import { useRequestsBatch } from "@common/hooks/useRequestsBatch";
import { congratsTypes } from "@common/screens/CongratsScreen";
import { theme } from "@common/theme/theme";
import { notifyBugsnag } from "@common/utils/initBugsnag";
import { cleanMediaUrl } from "@data/constants/apiConstants";
import { formIds } from "@data/constants/formConstants";
import {
    addPendingAttachment,
    audioRecordingStatus,
    clearPendingAttachments,
    setOngoingActivity,
} from "@data/redux/actions/activity";
import { apiIdle, apiRequest } from "@data/redux/actions/api";
import { clearLastPhoto, clearPhotos } from "@data/redux/actions/camera";
import {
    putCongrats,
    putNextScreenParams,
} from "@data/redux/actions/navigation";
import { offlineModeClear } from "@data/redux/actions/offlineMode";
import { activitiesService } from "@data/services/activitiesService";
import { incrementActivitiesCount } from "@data/utility/asyncStorageUtils";
import { findItemInListWithId } from "@data/utility/dataUtils";
import { buildPendingAttachmentsBody } from "@data/utility/pendingAttachmentsUtils";
import yup from "@data/utility/validation";
import { yupResolver } from "@hookform/resolvers/yup";
import { StatusBar } from "@common/systemBars/StatusBar";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Keyboard, View } from "react-native";
import { ActivityIndicator } from "react-native-paper";
import FontAwesome5 from "react-native-vector-icons/FontAwesome5";
import { connect } from "react-redux";
import { BadgeM } from "../../../common/components/badge/BadgeM";
import { useCurrentActivity } from "../../../common/hooks/useCurrentActivity";
import useAnnounceActivityFinalized from "../../../common/hooks/livekit/useAnnounceActivityFinalized";
import { authService } from "../../../data/services/authService";
import MyWorkModal from "../one-step/MyWorkModal";
import { showActivityCongrats } from "./ActivityFinalizedHelpers";
import TimerActivityStep from "./TimerActivityStep";
import PrerequisiteQuizGate from "./PrerequisiteQuizGate";
import { workMediumOf } from "../../../engine/activity/workMedium";

const ActivityScreen = (props) => {
    const { navigation, onExit, route, skipInitAnimation, onActivityFinalized, isInCheckpointOverlay, quizGateProps, hasQuizOverlay } = props

    const activity = props.currentActivity?.activityData

    // ** Component variables
    const activityId = activity?.id
    const activityTemplateId =
        activity?.activity_template_id ?? activity?.activityTemplateId
    const schoolSubjectId = activity?.school_subject_id
    const againActivityId = activity?.again_activity_id

    const activityTemplate = findItemInListWithId(
        activity?.activity_template_id ?? activity?.activityTemplateId,
        props.activityTemplates
    )

    const formData = activityTemplate?.steps
    const activityType = workMediumOf(formData)
    const stepDescriptions = [
        formData?.[0]?.description_init,
        formData?.[1]?.description_init,
    ]

    // 1-indexed step number of the media step (photo / audio_video), matching
    // the server's per-step log lookup. Hardcoding step 2 broke any template
    // whose media isn't the 2nd step (e.g. written_exercise photo at step 3):
    // the step_media_sent log landed on the wrong step and finalize never found
    // it. Falls back to 2 (the historical [chrono, media] layout) if the
    // template isn't loaded yet.
    const mediaStepIndex = useMemo(() => {
        const idx = formData?.findIndex(
            (step) => step?.type === "photo" || step?.type === "audio_video"
        )
        return idx >= 0 ? idx + 1 : 2
    }, [formData])
    const prevStepIndex = Math.max(mediaStepIndex - 1, 1)

    const [animationFinished, setAnimationFinished] = useState(skipInitAnimation || false)
    const { sendLiveKitData } = useLiveKitEventSender()
    const announceFinalized = useAnnounceActivityFinalized()

    const [submittedMessage, setSubmittedMessage] = useState(
        "On réceptionne ton activité."
    )

    const _submitClicked = useRef(false)
    // Allows exactly one automatic finalize re-send after a "media not saved
    // yet" 403, so a genuine dead-end can't turn into a retry loop. Reset per
    // activity in cleanUp().
    const finalizeRetriedRef = useRef(false)

    const chronoDurationRef = useRef(0)
    const setChronoDuration = (value) => {
        chronoDurationRef.current = value
    }

    const defaultValues = {
        init: {
            activity_template_id: activityTemplateId,
            school_subject_id: schoolSubjectId,
            activity_id: activityId ?? null,
            again_activity_id: againActivityId ?? null,
        },
        activity_logs: [],
        work_attachment_logs: [],
    }
    const formSchema = {
        init: yup.object().required(),
        activity_logs: yup.array().required(),
        work_attachment_logs: yup.array().nullable(),
    }

    const { markCurrentActivityAsDeleted, setCurrentActivity } =
        useCurrentActivity()
    const [notFoundError, setNotFoundError] = useState(false)

    const treatError = (errorData) => {
        const errorCode = errorData?.status

        if (errorCode === 404) {
            setNotFoundError(true)
            props.apiRequest(authService.getUser)
        }
    }

    const resolver = yupResolver(yup.object().shape(formSchema))
    const { control, resetField } = useForm({
        resolver,
        defaultValues,
    })

    useEffect(() => {
        props.setOngoingActivity(true)
        return () => {
            props.setOngoingActivity(false)
        }
    }, [])

    const [resultActivities, setResultActivities] = useState([])
    const resultActivity =
        resultActivities[0]?.id === activityId ? resultActivities[0] : null

    const [recordingURI, setRecordingURI] = useState(
        activityType === "audio"
            ? cleanMediaUrl(
                resultActivity?.activity_logs?.find(
                    (log) => log?.action === "step_media_sent"
                )?.work_attachment
            )
            : null
    )

    const [currentAudio, setCurrentAudio] = useState(null)

    const refresh = () => {
        if (isInCheckpointOverlay) return

        props.apiRequest(
            activitiesService.getActivities,
            {},
            {},
            {
                with_logs: 1,
                activity_ids: activityId,
            }
        )
    }

    useEffect(() => {
        refresh()
    }, [activityId])

    useApiStatus(activitiesService.getActivities, null, false, (data) => {
        setResultActivities(data)
    })

    const mediaLogs = useMemo(() => {
        return (
            resultActivity?.activity_logs
                ?.filter((log) => log?.action === "step_media_sent")
                ?.map((log) => {
                    return {
                        uri: log?.work_attachment,
                        width: 200,
                        height: 200,
                        id: log?.id,
                    }
                }) ?? []
        )
    }, [resultActivity?.activity_logs])

    const [images, setImages] = useState([])

    useEffect(() => {
        if (
            mediaLogs.length > 0 &&
            images.length === 0 &&
            activityType === "photo"
        ) {
            setImages(mediaLogs)
        }
    }, [mediaLogs])

    useEffect(() => {
        if (mediaLogs.length > 0 && !recordingURI && activityType === "audio") {
            setRecordingURI(
                cleanMediaUrl(
                    resultActivity?.activity_logs?.find(
                        (log) => log?.action === "step_media_sent"
                    )?.work_attachment
                )
            )
        }
    }, [mediaLogs])

    const allLogsAreSync =
        images?.filter((log) => !log?.id)?.length === 0 &&
        (!currentAudio || currentAudio?.id)

    const hasMedia = images?.length > 0 || recordingURI

    // Auto-submit previous activity silently on switch — only when the server
    // already holds a step_media_sent log for THAT activity. mediaLogs derives
    // from resultActivity, which is id-guarded, while the local `images` state
    // lags one render behind an activity switch: a flag based on it borrows
    // the previous activity's media and green-lights a finalize that can only
    // 403 (« pas encore sauvegardé ») server-side.
    useFinalizeSilentlyIfHasMedia(
        activityId,
        chronoDurationRef.current,
        mediaLogs.length > 0
    )

    const dispatchFinalize = () => {
        if (props.finalizeActivityRequest) return
        const body = { chrono_duration: chronoDurationRef.current }
        const pending = buildPendingAttachmentsBody(
            props.pendingAttachmentsByActivity?.[activityId]
        )
        if (pending) body.pending_attachments = pending
        props.apiRequest(
            activitiesService.finalizeActivity,
            { id: activityId },
            body
        )
    }

    const onSubmit = () => {
        // If in checkpoint overlay, don't allow submission - the main ActivityScreen will handle it
        if (isInCheckpointOverlay) {
            console.log('[ActivityScreen] In checkpoint overlay - submission blocked')
            return
        }

        Keyboard.dismiss()
        setStatus("finished")
        if (submitted) {
            _submitClicked.current = true
        } else if (allLogsAreSync) {
            setVisibleModal(false)
            dispatchFinalize()
        } else {
            _submitClicked.current = true
            startRequests()
        }
    }

    useApiStatus(
        activitiesService.finalizeActivity,
        null,
        false,
        (successData) => {
            if (isInCheckpointOverlay) {
                if (onActivityFinalized) {
                    onActivityFinalized()
                }
                return
            }

            // Only handle if the success is for the current activity
            if (successData?.activity?.id !== activityId) {
                return
            }

            props.setOngoingActivity(false)
            const activity = { ...successData?.activity }
            const user = { ...successData?.user }
            const skillSets = successData?.skill_sets
            props.clearPendingAttachments({ activityId })
            pushSuccessScreen(
                user,
                activity,
                skillSets,
                successData?.correction_wait
            )

            setCurrentActivity({
                activityData: activity,
                progressStatus: "finished",
            })

            incrementActivitiesCount().then()

            // Call the callback if provided (used to interrupt checkpoint in overlay)
            if (onActivityFinalized) {
                onActivityFinalized()
            }
        },
        (errorData) => {
            // Idempotence: if the activity was already finalized server-side
            // (e.g. retry after a transient network error), treat as success.
            if (
                errorData?.status === 403 &&
                typeof errorData?.message === "string" &&
                errorData.message.includes("déjà été soumise")
            ) {
                props.clearPendingAttachments({ activityId })
                props.setOngoingActivity(false)
                props.apiIdle(activitiesService.finalizeActivity)
                if (onActivityFinalized) onActivityFinalized()
                refresh()
                return
            }

            // "Ton travail / vocal n'est pas encore sauvegardé" — the server
            // couldn't find the media log. If we still hold un-confirmed tmp
            // keys, re-send finalize once so the server self-heals from them
            // (covers a key captured just after the first finalize fired).
            // Without this, the student was stuck re-hitting the same 403.
            if (
                errorData?.status === 403 &&
                typeof errorData?.message === "string" &&
                errorData.message.includes("n'est pas encore sauvegardé")
            ) {
                if (!finalizeRetriedRef.current) {
                    const pending = buildPendingAttachmentsBody(
                        props.pendingAttachmentsByActivity?.[activityId]
                    )
                    if (pending) {
                        finalizeRetriedRef.current = true
                        props.apiIdle(activitiesService.finalizeActivity)
                        dispatchFinalize()
                        return
                    }
                }
                // No tmp key to self-heal from: this finalize can never pass
                // server-side. The media is still on the device — surface the
                // dead-end with a retry instead of swallowing the 403 (the
                // student used to stare at a screen that did nothing).
                props.apiIdle(activitiesService.finalizeActivity)
                showWorkNotSavedDialog()
                return
            }

            treatError(errorData)
        }
    )

    useEffect(() => {
        cleanUp()
    }, [activityId])

    const cleanUp = () => {
        // Reset API state to prevent stale success callbacks when switching activities
        props.apiIdle(activitiesService.finalizeActivity)

        setSubmittedMessage("On réceptionne ton activité.")
        setRecordingURI(null)
        setCurrentAudio(null)
        setImages(mediaLogs ?? [])
        _submitClicked.current = false
        finalizeRetriedRef.current = false
        setStatus(null)
        setVisibleModal(false)

        if (!skipInitAnimation) {
            setAnimationFinished(false)
        }
        setChronoDuration(0)
        setNotFoundError(false)

        resetField("init", {
            defaultValue: {
                activity_template_id: activityTemplateId,
                school_subject_id: schoolSubjectId,
                activity_id: activityId ?? null,
                again_activity_id: againActivityId ?? null,
            },
        })
        resetField("activity_logs", { defaultValue: [] })
        resetField("work_attachment_logs", { defaultValue: [] })
    }

    const pushSuccessScreen = (user, activity, skillSets, correctionWait) => {
        // Le tuteur l'apprend à l'envoi, que l'écran de fin vienne tout de
        // suite ou après une correction.
        announceFinalized(activity)

        const congrats = {
            congratsType: congratsTypes.ACTIVITY_FINALIZED,
            congratsData: {
                activity,
                chapterProgress: activity?.student_chapter ? true : false,
                userSkillSets: skillSets?.filter(
                    (ss) => ss?.id === 1 || ss?.id === 2
                ),
            },
            hasNewTitle: false,
            petalsGained: 0,
        }

        const skillSetWithNewTitles =
            skillSets
                ?.filter(
                    (ss) =>
                        ss?.current_count === 0 &&
                        !ss?.is_last_evolution_read &&
                        ss?.current_step >= 2 &&
                        ss?.steps?.[ss?.current_step - 1]?.title_gained !==
                        ss?.steps?.[ss?.current_step - 2]?.title_gained
                )
                ?.map((ss) => {
                    return {
                        userSkillSet: ss,
                    }
                }) || []

        if (skillSetWithNewTitles?.length > 0) {
            congrats.congratsData.newTitleSkillSet = skillSetWithNewTitles
        }

        // If petals gained, show petals gained congrats
        const petalsGainedCount =
            skillSets
                ?.filter(
                    (ss) =>
                        ss?.current_count === 0 && !ss?.is_last_evolution_read
                )
                ?.reduce(
                    (pValue, cValue) =>
                        pValue +
                        cValue?.steps?.[cValue?.current_step - 1]
                            ?.petals_gained,
                    0
                ) || 0

        congrats.congratsData.petalsGained = petalsGainedCount

        // Une copie que Sam corrige passe d'abord par l'attente et la
        // correction, sur cet écran. L'écran de fin se prépare à leur sortie :
        // d'ici là, la liste des activités à faire a pu changer.
        if (correctionWait && props.onCorrectionExpected) {
            cleanUp()
            props.onCorrectionExpected({ activity, correctionWait, congrats })
            return
        }

        cleanUp()
        showActivityCongrats({ ...props, navigation }, congrats)
    }

    const [status, _setStatus] = useState(null)
    const statusRef = useRef(status) // Ref is used to make use of state within app state event listener (timer)
    const setStatus = (data) => {
        statusRef.current = data
        _setStatus(data)
    }
    const [launchCamera] = useCamera(formIds.NEW_MATERIAL, true, false)
    const [visibleModal, setVisibleModal] = useState(false)
    const handleShowModal = () => {
        setVisibleModal(!visibleModal)
    }

    const audioToUpload = recordingURI
        ? {
            uri: recordingURI,
            name: `Activity_audio${
                props.user?.display_name
            }_${moment().format("YYYY-MM-DD")}.m4a`,
            type: "audio/m4a",
        }
        : null

    const filesToUpload = images
        ?.filter((img) => !img.id)
        .map((img) => {
            return {
                uri: img?.uri,
                name: `activity_image_${
                    props.user?.display_name
                }_${moment().format("YYYY-MM-DD")}.jpg`,
                type: "image/jpg",
            }
        })

    // Upload media (Vapor) + best-effort postActivityLogs for live tutor preview.
    // Finalize is now triggered manually after success so it can carry the
    // pending_attachments tmp keys persisted client-side via Redux.
    const [globalProgress, submitted, startRequests] = useRequestsBatch(
        [],
        activityType === "photo" ? filesToUpload : [audioToUpload],
        "ACTIVITY_FORM",
        {
            service: activitiesService.postActivityLogs,
            data: {},
            tmpFilesKey: "activity_logs",
            tmpFilesFormatter: (attachmentTmp) => {
                return {
                    step: mediaStepIndex,
                    step_data: null,
                    work_attachment: attachmentTmp,
                    action: "step_media_sent",
                    created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
                }
            },
            additionalData: [
                {
                    action: "step_finished",
                    step: prevStepIndex,
                    created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
                },
                {
                    action: "step_started",
                    step: mediaStepIndex,
                    created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
                },
                {
                    action: "step_finished",
                    step: mediaStepIndex,
                    created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
                },
            ],
            params: { id: activityId },
            // Durably persist each tmp key the instant Vapor confirms its upload,
            // indexed on the real media step. This is the resilient "outbox": it
            // survives a lost /logs call or an app kill before finalize, so
            // finalize can self-heal from pending_attachments instead of 403-ing.
            onFileUploadSuccess: (responseKey) => {
                if (responseKey && activityId) {
                    props.addPendingAttachment({
                        activityId,
                        stepIndex: mediaStepIndex,
                        tmpKey: responseKey,
                    })
                }
            },
            onSuccessBeforeNext: () => {
                if (_submitClicked.current) {
                    dispatchFinalize()
                }
                return false
            },
            onError: () => {
                // postActivityLogs is best-effort (live tutor preview). If it
                // fails AND the user clicked submit, finalize anyway with the
                // tmp keys we already persisted in pending_attachments — the
                // server self-heals from them. But when the S3 upload itself
                // failed there is no tmp key to heal from: finalizing would
                // only 403. The media is still on the device, offer a retry.
                if (!_submitClicked.current) return
                const pending = buildPendingAttachmentsBody(
                    props.pendingAttachmentsByActivity?.[activityId]
                )
                if (pending) {
                    dispatchFinalize()
                } else {
                    showWorkNotSavedDialog()
                }
            },
            showErrorDialog: false,
        },
        []
    )

    const showWorkNotSavedDialog = () => {
        _submitClicked.current = false
        DialogM.show({
            text1: "Ton travail n'est pas encore sauvegardé",
            text2: "Vérifie ta connexion puis réessaie d'envoyer ton travail.",
            buttonText1: "Réessayer",
            onPress: () => {
                _submitClicked.current = true
                startRequests()
            },
            exitButton: true,
            onPressBack: () => {},
        })
    }

    useEffect(() => {
        if (isInCheckpointOverlay) return

        if (
            props.lastPhotoFormId === formIds.NEW_MATERIAL &&
            !!props.lastPhoto &&
            !images?.map((x) => x?.uri)?.includes(props.lastPhoto?.uri)
        ) {
            const imagesCopy = images?.map((x) => x) ?? []
            imagesCopy?.push(props.lastPhoto)
            setImages(imagesCopy)
        } else if (
            props.lastPhotoFormId === formIds.NEW_MATERIAL &&
            props.lastPhotos?.length > 0
        ) {
            const imagesCopy = images?.map((x) => x) ?? []
            imagesCopy?.push(...props.lastPhotos)
            setImages(imagesCopy)
        }
    }, [props.lastPhoto, props.lastPhotos])

    useEffect(() => {
        if (isInCheckpointOverlay) return

        if (images.filter((img) => !img.id).length > 0) {
            startRequests()

            props.clearPhotos()
            props.clearLastPhoto()

            // Only open modal if checkpoint is not active
            if (!props.isAgentModalVisible) {
                handleShowModal()
            }
        }
    }, [images])

    // Show modal when checkpoint closes and there are images
    useEffect(() => {
        if (isInCheckpointOverlay) return

        // When checkpoint closes and we have images, show the modal
        if (!props.isAgentModalVisible && images.length > 0 && !visibleModal) {
            handleShowModal()
        }
    }, [props.isAgentModalVisible])

    useEffect(() => {
        // Only handle audio recording in main instance, not in checkpoint overlay
        if (isInCheckpointOverlay) return

        if (props.audioStatus === "recorded") {
            startRequests()
            props.audioRecordingStatus("sending")
        } else if (props.audioStatus === "deleted") {
            props.apiRequest(
                activitiesService.deleteActivityLog,
                { id: currentAudio?.id },
                {
                    live_mode: !!props.liveSessionId,
                    live_session_id: props.liveSessionId ?? undefined,
                }
            )
            props.audioRecordingStatus("")
        }
    }, [props.audioStatus])

    useApiStatus(
        activitiesService.postActivityLogs,
        null,
        false,
        (successData) => {
            const successDataActivityId = successData?.id
            const successDataActivityDuration = successData?.duration

            if (activityId === successDataActivityId) {
                // Find the last activity log of the activity in success data
                const activityLogs = successData?.activity_logs?.filter(
                    (log) => log?.action === "step_media_sent"
                )
                const lastActivityLog = activityLogs[activityLogs.length - 1]

                if (activityLogs.length > 0)
                    if (activityType === "photo") {
                        const newImages = []
                        activityLogs.map((log) => {
                            newImages?.push({
                                uri: log?.work_attachment,
                                width: 200,
                                height: 200,
                                id: log?.id,
                            })
                        })

                        setImages(newImages)

                        if (props.isLive) {
                            sendLiveKitData("studentWorkUpdated", {
                                studentId: props.user?.id,
                                activityId,
                                mediaType: "photo",
                                count: newImages.length,
                            })
                        }

                        // Refresh activity data so checkpoint conversation sees the new logs
                        refresh()
                    } else {
                        setCurrentAudio({
                            uri: lastActivityLog?.work_attachment,
                            id: lastActivityLog?.id,
                        })

                        // Refresh activity data so checkpoint conversation sees the new logs
                        refresh()
                    }
            } else {
                props.apiRequest(
                    activitiesService.finalizeActivitySilent,
                    { id: successDataActivityId },
                    { chrono_duration: successDataActivityDuration }
                )
            }
        },
        (errorData) => {
            const errorCode = errorData?.status

            if (errorCode === 404) {
                setNotFoundError(true)
                props.apiRequest(authService.getUser)
            } else {
                // Old treatment for non-404 errors
                props.audioRecordingStatus("sync_error")

                if (errorData?.errors) {
                    const errorMessages = Object.keys(errorData?.errors)?.map(
                        (key) => errorData?.errors[key]?.[0]
                    )
                    notifyBugsnag(
                        "Erreur d'envoi de media pour l'activité " +
                        activityId +
                        " : " +
                        errorMessages.join(", ")
                    )
                } else {
                    notifyBugsnag(
                        "Erreur d'envoi de media pour l'activité " +
                        activityId +
                        " : " +
                        errorData?.message
                    )
                }

                // Submit-driven flow: postActivityLogs is best-effort (it
                // mirrors the upload to the tutor's live preview). Don't
                // block the user with a retry dialog — finalize will be
                // dispatched with the persisted tmp keys and self-heal.
                if (_submitClicked.current) {
                    return
                }

                DialogM.show({
                    text1: "Erreur",
                    text2: "Une erreur s'est produite lors de l'envoi de ton travail",
                    buttonText1: "Réessayer",
                    onPress: () => {
                        startRequests()
                    },
                    buttonText2: "Annuler",
                    onPressBack: () => {},
                })
            }
        }
    )

    useApiStatus(
        activitiesService.updateChrono,
        null,
        false,
        () => {},
        (errorData) => {
            treatError(errorData)
            props.apiIdle(activitiesService.updateChrono)
        }
    )

    useApiStatus(
        activitiesService.deleteActivityLog,
        null,
        false,
        () => {
            setRecordingURI(null)

            if (props.isLive && activityType === "photo") {
                sendLiveKitData("studentWorkUpdated", {
                    studentId: props.user?.id,
                    activityId,
                    mediaType: "photo",
                    count: images?.length,
                })
            }
        },
        (errorData) => {
            treatError(errorData)
        }
    )

    useApiStatus(authService.getUser, null, false, () => {
        if (notFoundError) {
            markCurrentActivityAsDeleted()
            setNotFoundError(false)
        }
    })

    const buttonLabel =
        activityType === "photo"
            ? "scanner mon travail"
            : "Enregistrer mon travail"

    const renderButtonIcon = () => {
        return (
            <View
                style={{
                    backgroundColor: theme.colors.black,
                    height: 50,
                    width: 50,
                    alignItems: "center",
                    justifyContent: "center",
                    alignSelf: "flex-start",
                    right: 12,
                    borderTopLeftRadius: 15,
                    borderBottomLeftRadius: 15,
                }}
            >
                <>
                    <FontAwesome5
                        name={
                            activityType === "photo" ? "camera" : "microphone"
                        }
                        size={18}
                        color={theme.colors.white}
                        style={{
                            alignSelf: "center",
                        }}
                    />
                    {((images?.length === 0 && activityType === "photo") ||
                        (recordingURI && activityType === "audio")) &&
                    props.getActivitiesRequest ? (
                        <ActivityIndicator
                            animating={true}
                            style={{
                                position: "absolute",
                                bottom: 5,
                                right: 10,
                            }}
                            size={16}
                            color={theme.colors.primary}
                        />
                    ) : (
                        images.length > 0 && (
                            <BadgeM
                                style={{
                                    backgroundColor: theme.colors.primary,
                                    position: "absolute",
                                    bottom: 5,
                                    right: 10,
                                    color: theme.colors.white,
                                }}
                                count={images?.length}
                                size={16}
                            />
                        )
                    )}
                </>
            </View>
        )
    }

    return (
        <View
            style={{
                flex: 1,
                alignItems: "center",
            }}
        >
            <TimerActivityStep
                activity={activity}
                control={control}
                status={status}
                animationFinished={animationFinished}
                setAnimationFinished={setAnimationFinished}
                setChronoDuration={setChronoDuration}
                setStatus={setStatus}
                submitted={submitted || props.finalizeActivityRequest}
                globalProgress={
                    props.finalizeActivityRequest ? null : globalProgress
                }
                submittedMessage={
                    props.finalizeActivityRequest
                        ? submittedMessage
                        : "On sauvegarde ton travail."
                }
                stepDescriptions={stepDescriptions}
                hasQuizOverlay={hasQuizOverlay}
                onExit={() => {
                    onExit(hasMedia, handleShowModal)
                }}
                route={route}
                hideActivityMenuButton={props.hideMyWorkButton}
                hideStartAnimation={skipInitAnimation}
                isInCheckpointOverlay={isInCheckpointOverlay}
            />
            {animationFinished &&
                !submitted &&
                !props.finalizeActivityRequest &&
                !isInCheckpointOverlay && (
                    <View
                        style={{
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 20,
                        }}
                    >
                        {
                            <FilledButtonM
                                color={theme.colors.primary}
                                trackingId={"my_work_button"}
                                onPress={
                                    images.length > 0 ||
                                    activityType === "audio"
                                        ? handleShowModal
                                        : launchCamera
                                }
                                icon={renderButtonIcon}
                                style={{
                                    alignSelf: "center",
                                }}
                                contentStyle={{
                                    justifyContent:
                                        images?.length > 0 &&
                                        activityType === "photo"
                                            ? "space-between"
                                            : "center",
                                    alignItems: "center",
                                }}
                                condition={
                                    images?.length > 0 &&
                                    activityType === "photo"
                                }
                                labelStyle={{
                                    fontSize: 12,
                                    flex: 1,
                                    right: 12,
                                }}
                                label={
                                    images?.length > 0 || recordingURI
                                        ? "Mon travail"
                                        : buttonLabel
                                }
                            />
                        }
                    </View>
                )}
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
            {!isInCheckpointOverlay && (
                <MyWorkModal
                    activityId={activityId}
                    images={images}
                    allLogsAreSync={allLogsAreSync}
                    isOpen={visibleModal}
                    handleShowModal={handleShowModal}
                    setImages={setImages}
                    navigation={navigation}
                    onSubmit={onSubmit}
                    activityType={activityType}
                    status={status}
                    setStatus={setStatus}
                    control={control}
                    recordingURI={recordingURI}
                    setRecordingURI={setRecordingURI}
                    stepDescriptions={stepDescriptions}
                    progress={globalProgress}
                    submitted={submitted}
                    onRetry={startRequests}
                />
            )}

            {/* Quiz Gate Overlay - blocks activity until prerequisite quiz is passed */}
            {quizGateProps && (
                <PrerequisiteQuizGate
                    onStartQuiz={quizGateProps.onStartQuiz}
                    loading={quizGateProps.loading}
                />
            )}
        </View>
    )
}
const mapStateToProps = (state) => {
    return {
        postActivityLogV1Request: state.api.postActivityLogV1?.request,
        postActivityLogsRequest: state.api.postActivityLogs?.request,
        finalizeActivityRequest: state.api.finalizeActivity?.request,
        finalizeActivitySuccess: state.api.finalizeActivity?.success,
        finalizeActivityError: state.api.finalizeActivity?.error,

        getActivitiesRequest: state.api.getActivities?.request,

        finalizeActivitySuccessData: state.api.finalizeActivity?.data,

        activityTemplates: state.data.staticData?.activity_templates,
        user: state.data.currentUser?.object,
        activityLogs: state.data.currentUser?.object?.activity_logs,

        isInternetReachable: state.offlineMode.isInternetReachable,
        isOfflineModeEnabled: state.offlineMode.isOfflineModeEnabled,
        liveSessionId: state.liveMode?.liveSessionId,
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
        isLive: state.liveMode.isLiveModeEnabled,
        liveSessions: state.data.currentUser?.object?.live_sessions,
        ongoingActivity: state.activity?.ongoingActivity,
        lastPhoto: state.camera?.lastPhoto,
        lastPhotos: state.camera?.lastPhotos,
        lastPhotoFormId: state.camera?.formId,
        audioStatus: state.activity?.audioStatus,
        currentActivity: state.data?.currentActivity?.activity,
        isAgentModalVisible: state.liveMode?.isAgentModalVisible,
        pendingAttachmentsByActivity:
            state.data?.pendingAttachments?.byActivityId,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    offlineModeClear,
    setOngoingActivity,
    clearLastPhoto,
    clearPhotos,
    audioRecordingStatus,
    putNextScreenParams,
    putCongrats,
    addPendingAttachment,
    clearPendingAttachments,
}

export default connect(mapStateToProps, mapDispatchToProps)(ActivityScreen)
